/** SimulationBridge — passive observer injected when ?sim=1, exposing game state to Playwright. */
import type { GameScene } from '../scenes/GameScene';
import type { Enemy } from '../entities/Enemy';
import { DebugConfig, type DebugFlagKey } from '../debug/DebugConfig';
import { ConfigLoader } from '../systems/ConfigLoader';
import type {
  HitEvent, StatusEvent, DotTickEvent, FloatyEvent, VfxEvent, TreeProgressionSnapshot,
  SimScenario, EnemyState, PlayerState, WorldState, GameTestAPI,
  StatusEffectDetail,
} from './types';
import type { FortifySkillEffect } from '../skills/player/earth/FortifySkillEffect';
import type { DamageNumbers } from '../systems/DamageNumbers';
import type { StatusVFXOrchestrator } from '../vfx/StatusVFXOrchestrator';

export type { HitEvent, StatusEvent, DotTickEvent, FloatyEvent, VfxEvent, TreeProgressionSnapshot, SimEnemy, SimPlayer, SimScenario, EnemyState, PlayerState, WorldState, GameTestAPI, StatusEffectDetail } from './types';

// ── Activation guard ──────────────────────────────────────────────────

const SIM_ACTIVE = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).has('sim');

// ── Bridge singleton ──────────────────────────────────────────────────

export class SimulationBridge {
  private static instance: SimulationBridge | null = null;

  private scene: GameScene | null = null;
  private _hitEvents: HitEvent[] = [];
  private _statusEvents: StatusEvent[] = [];
  private _dotTickEvents: DotTickEvent[] = [];
  private _floatyEvents: FloatyEvent[] = [];
  private _vfxEvents: VfxEvent[] = [];
  private _enemyTags: Map<string, Enemy> = new Map();

  private constructor() { /* singleton — use getInstance() */ }

  /** Lazy singleton; returns null when ?sim=1 is absent. */
  static getInstance(): SimulationBridge | null {
    if (!SIM_ACTIVE) return null;
    if (!SimulationBridge.instance) {
      SimulationBridge.instance = new SimulationBridge();
    }
    return SimulationBridge.instance;
  }

  /**
   * Called by GameScene.create() after all systems are wired.
   * Stores a reference to the scene so the bridge can reach
   * EnemyManager, player components, and combat systems.
   * Registers window.__gameTest for Playwright access.
   */
  register(scene: GameScene): void {
    this.scene = scene;
    // Disable debug cheats (god mode, one-hit kill, etc.) so combat behaves realistically
    DebugConfig.setEnabled(false);
    // Suppress ForkChoiceScene pauses — headless sim has no UI to dismiss them,
    // and the resulting scene pause freezes GameScene.update() entirely.
    scene.enableSimMode();
    this.registerWindowAPI();
  }

  /** Expose the test API on the global window object for Playwright. */
  private registerWindowAPI(): void {
    const bridge = this;
    (window as unknown as Record<string, unknown>).__gameTest = {
      setup: (scenario: SimScenario) => bridge.setup(scenario),
      reset: () => bridge.reset(),
      dispatchSkill: (skillId: string) => bridge.dispatchSkill(skillId),
      setPlayerFacing: (angleDeg: number) => bridge.setPlayerFacing(angleDeg),
      getPlayerState: () => bridge.buildPlayerState(),
      getEnemyState: (tag: string) => bridge.buildEnemyState(tag),
      getAllEnemyStates: () => bridge.buildAllEnemyStates(),
      getHitEvents: () => bridge.getHitEvents(),
      getStatusEvents: () => bridge.getStatusEvents(),
      getDotTickEvents: () => bridge.getDotTickEvents(),
      clearEvents: () => bridge.clearEvents(),
      isSettled: () => bridge.isSettled(),
      setPlayerMoving: (dx: number, dy: number) => bridge.setPlayerMoving(dx, dy),
      setPlayerStamina: (value: number) => bridge.setPlayerStamina(value),
      setPlayerHealth: (value: number) => bridge.setPlayerHealth(value),
      forceStationary: (durationMs: number) => bridge.forceStationary(durationMs),
      tickEffects: (deltaMs: number) => bridge.tickEffects(deltaMs),
      queryFactoryAI: (method: string, ...args: unknown[]) => bridge.queryFactoryAI(method, ...args),
      getWorldState: () => bridge.buildWorldState(),
      setDebugFlag: (flag: string, value: boolean) => bridge.setDebugFlag(flag, value),
      getProgressionState: () => bridge.buildProgressionState(),
      getFloatyEvents: () => bridge.getFloatyEvents(),
      clearFloatyEvents: () => bridge.clearFloatyEvents(),
      getVfxEvents: () => bridge.getVfxEvents(),
      clearVfxEvents: () => bridge.clearVfxEvents(),
      applyStatusToEnemy: (tag: string, statusId: string, opts?: {
        durationMs?: number; intensity?: number; stacks?: number; sourceSkillId?: string;
      }) => bridge.applyStatusToEnemy(tag, statusId, opts),
      queryUpgradeDamageMultiplier: (skillId: string) => bridge.queryUpgradeDamageMultiplier(skillId),
      queryDeathTriggerCount: () => bridge.queryDeathTriggerCount(),
      queryCooldownHookCount: () => bridge.queryCooldownHookCount(),
      queryBuffDamageMultiplier: (tag: string) => bridge.queryBuffDamageMultiplier(tag),
      queryChargeState: (chargeId: string) => bridge.queryChargeState(chargeId),
      addCharge: (chargeId: string, amount: number) => bridge.addCharge(chargeId, amount),
      queryStationaryDurationMs: () => bridge.queryStationaryDurationMs(),
      queryGravityWellCount: () => bridge.queryGravityWellCount(),
      addGravityWell: (config: { id: string; x: number; y: number; pullRadiusTiles: number; pullStrengthTilesPerSec: number; durationMs: number; sourceSkillId: string }) => bridge.addGravityWell(config),
      queryOrbitalDebrisState: () => bridge.queryOrbitalDebrisState(),
      startOrbitalAccumulation: () => bridge.startOrbitalAccumulation(),
      queryTemporalEchoState: () => bridge.queryTemporalEchoState(),
      beginTemporalEchoRecording: (x: number, y: number, hp: number, windowMs: number) => bridge.beginTemporalEchoRecording(x, y, hp, windowMs),
    } satisfies GameTestAPI;
  }

  /** The GameScene this bridge wraps, or null if not yet registered. */
  getScene(): GameScene | null {
    return this.scene;
  }

  // ── Tag management ────────────────────────────────────────────────

  /** Associate a caller-supplied tag with a spawned enemy instance. */
  tagEnemy(tag: string, enemy: Enemy): void {
    this._enemyTags.set(tag, enemy);
  }

  /** Retrieve a tagged enemy. */
  getTaggedEnemy(tag: string): Enemy | undefined {
    return this._enemyTags.get(tag);
  }

  /** Reverse lookup: find the tag for a given enemy instance. */
  getTagForEnemy(enemy: Enemy): string | undefined {
    for (const [tag, e] of this._enemyTags) {
      if (e === enemy) return tag;
    }
    return undefined;
  }

  /** All tag → enemy entries. */
  get enemyTags(): ReadonlyMap<string, Enemy> {
    return this._enemyTags;
  }

  /** Remove all tag associations. */
  clearTags(): void {
    this._enemyTags.clear();
  }

  // ── Event recording ───────────────────────────────────────────────

  /** Record a combat hit (called from CombatHitResolver when bridge is active). */
  recordHit(event: HitEvent): void {
    this._hitEvents.push(event);
  }

  /** Record a status effect application or removal. */
  recordStatus(event: StatusEvent): void {
    this._statusEvents.push(event);
  }

  /** Snapshot of all recorded hit events. */
  getHitEvents(): HitEvent[] {
    return [...this._hitEvents];
  }

  /** Snapshot of all recorded status events. */
  getStatusEvents(): StatusEvent[] {
    return [...this._statusEvents];
  }

  /** Record a DoT tick event. */
  recordDotTick(event: DotTickEvent): void {
    this._dotTickEvents.push(event);
  }

  /** Snapshot of all recorded DoT tick events. */
  getDotTickEvents(): DotTickEvent[] {
    return [...this._dotTickEvents];
  }

  /** Clear all event buffers. */
  clearEvents(): void {
    this._hitEvents.length = 0;
    this._statusEvents.length = 0;
    this._dotTickEvents.length = 0;
    this._floatyEvents.length = 0;
    this._vfxEvents.length = 0;
  }

  /** Record a floating text event (damage number or label). */
  recordFloaty(event: FloatyEvent): void {
    this._floatyEvents.push(event);
  }

  /** Snapshot of all recorded floaty events. */
  getFloatyEvents(): FloatyEvent[] {
    return [...this._floatyEvents];
  }

  /** Clear the floaty event buffer. */
  clearFloatyEvents(): void {
    this._floatyEvents.length = 0;
  }

  /** Record a VFX activation/deactivation event. */
  recordVfx(event: VfxEvent): void {
    this._vfxEvents.push(event);
  }

  /** Snapshot of all recorded VFX events. */
  getVfxEvents(): VfxEvent[] {
    return [...this._vfxEvents];
  }

  /** Clear the VFX event buffer. */
  clearVfxEvents(): void {
    this._vfxEvents.length = 0;
  }

  /** Build progression state snapshots for all trees. */
  buildProgressionState(): TreeProgressionSnapshot[] {
    if (!this.scene) return [];
    const prog = this.scene.player.skillProgression;
    const snapshots: TreeProgressionSnapshot[] = [];
    for (const treeName of Object.keys(ConfigLoader.v4Trees)) {
      const progress = prog.getTreeProgress(treeName);
      snapshots.push({
        treeName,
        xp: progress.xp,
        uses: progress.uses,
        tier: prog.getCurrentTier(treeName),
        unlockedNodes: ConfigLoader.getV4AllNodes(treeName)
          .filter(n => prog.isUnlocked(n.id))
          .map(n => n.id),
      });
    }
    return snapshots;
  }

  /**
   * Install observation hooks on DamageNumbers and StatusVFXOrchestrator.
   * Called by GameScene after constructing these systems.
   * Wraps original methods to record events without altering behavior.
   */
  installObserverHooks(damageNumbers: DamageNumbers, statusVfxOrchestrator: StatusVFXOrchestrator): void {
    // --- Hook DamageNumbers.spawn() ---
    const origSpawn = damageNumbers.spawn.bind(damageNumbers);
    damageNumbers.spawn = (worldX: number, worldY: number, damage: number, baseDamage: number, isCrit: boolean, isBackstab = false, received = false) => {
      this.recordFloaty({
        timestamp: Date.now(),
        text: `${damage}`,
        x: worldX,
        y: worldY,
        type: 'damage',
      });
      origSpawn(worldX, worldY, damage, baseDamage, isCrit, isBackstab, received);
    };

    // --- Hook DamageNumbers.spawnLabel() ---
    const origSpawnLabel = damageNumbers.spawnLabel.bind(damageNumbers);
    damageNumbers.spawnLabel = (worldX: number, worldY: number, label: string, color: string, fontSize: string) => {
      this.recordFloaty({
        timestamp: Date.now(),
        text: label,
        x: worldX,
        y: worldY,
        type: 'label',
      });
      origSpawnLabel(worldX, worldY, label, color, fontSize);
    };

    // --- Hook StatusVFXOrchestrator for VFX start/stop events ---
    // Wrap the update() method to detect newly started and stopped effects.
    // StatusVFXOrchestrator uses frame-polling with diff: we intercept the
    // internal applyEffectVfx/removeEffectVfx by observing sustainRefCounts changes.
    // Simpler approach: wrap registerEntity/unregisterEntity and patch the factory's
    // play/stop methods. But that's fragile. Instead, we'll use the public diff logic:
    // after each update(), compare the entity states to detect changes.
    //
    // Actually the simplest and most reliable approach: monkey-patch the private
    // applyEffectVfx and removeEffectVfx methods. They're the single points where
    // VFX transitions happen.
    const orch = statusVfxOrchestrator as unknown as Record<string, unknown>;
    const origApply = (orch['applyEffectVfx'] as Function).bind(statusVfxOrchestrator);
    const origRemove = (orch['removeEffectVfx'] as Function).bind(statusVfxOrchestrator);
    const bridge = this;
    const entities = orch['entities'] as Map<string, unknown>;

    orch['applyEffectVfx'] = function (state: unknown, effectId: string) {
      // Find entityId from state reference
      const entityId = findEntityId(entities, state);
      if (entityId) {
        bridge.recordVfx({
          timestamp: Date.now(),
          entityId,
          effectId,
          action: 'start',
        });
      }
      return origApply(state, effectId);
    };

    orch['removeEffectVfx'] = function (state: unknown, effectId: string) {
      const entityId = findEntityId(entities, state);
      if (entityId) {
        bridge.recordVfx({
          timestamp: Date.now(),
          entityId,
          effectId,
          action: 'stop',
        });
      }
      return origRemove(state, effectId);
    };
  }

  // ── Scenario API ──────────────────────────────────────────────────

  /** Configure the scene for a test scenario: position player, unlock skills, spawn tagged enemies. */
  setup(scenario: SimScenario): void {
    if (!this.scene) return;

    const { player, enemyManager } = this.scene;

    // Clear previous scenario state
    this.clearEvents();
    this.clearTags();

    // Deactivate any active toggle/channel skills to prevent state bleed
    player.skillActivation.deactivateAll();

    // Clear persistent world state (zones, summons, terrain) to prevent state bleed
    this.scene.resetSimState();

    // Sweep pending deaths before removing enemies to avoid double-free
    enemyManager.sweepDeadEnemies();

    // Remove all existing enemies
    for (const enemy of [...enemyManager.all]) {
      enemyManager.removeEnemy(enemy, false);
    }

    // Flush stale respawn queue, corpse records, and pending deaths
    enemyManager.clearRespawnQueue();
    enemyManager.clearCorpses();
    enemyManager.clearPendingDeaths();

    // Clear simulated movement input from previous scenario
    player.setSimMovementOverride(null);

    // Position player
    player.body.reset(scenario.player.x, scenario.player.y);

    // Set facing direction from angle
    if (scenario.player.facingAngleDeg !== undefined) {
      this.setPlayerFacing(scenario.player.facingAngleDeg);
    }

    // Unlock requested skills — grantXp to cross thresholds and chooseFork where needed
    if (scenario.player.unlockedSkills) {
      for (const skill of scenario.player.unlockedSkills) {
        if (!player.skillProgression.isUnlocked(skill.nodeId)) {
          // Grant enough XP to unlock everything up to this node
          player.skillProgression.grantXp(skill.treeName, 100000);
        }
        // If still not unlocked, it may be behind a fork — auto-choose it
        if (!player.skillProgression.isUnlocked(skill.nodeId)) {
          const forkInfo = ConfigLoader.getV4ForkForNode(skill.nodeId);
          if (forkInfo) {
            player.skillProgression.chooseFork(
              skill.treeName, forkInfo.fork.forkId, skill.nodeId,
            );
          }
        }
      }
    }

    // Spawn scenario enemies with tags — freeze AI movement for deterministic testing
    let autoTagCounter = 0;
    for (const enemyDef of scenario.enemies) {
      const spawned = enemyManager.spawnAt(enemyDef.x, enemyDef.y, enemyDef.archetypeId);
      if (spawned) {
        // Freeze AI by default so skill/combat tests have a stationary target.
        // Pass frozen: false for AI movement tests.
        spawned.simFrozen = enemyDef.frozen !== false;
        // Inject pre-configured AI state (mode, aggro, LKP) if any field is set.
        if (enemyDef.startMode !== undefined || enemyDef.startAggro !== undefined || enemyDef.startLkp !== undefined) {
          const lkp = enemyDef.startLkp ?? (enemyDef.startMode === 'chase' ? { x: scenario.player.x, y: scenario.player.y } : undefined);
          spawned.ai.forceSimState({
            mode: enemyDef.startMode,
            aggro: enemyDef.startAggro,
            lkp,
          });
        }
        const tag = enemyDef.tag ?? `enemy_${autoTagCounter++}`;
        this.tagEnemy(tag, spawned);
      }
    }

    // Initialize passive effects — tick skill effects once so that passives using
    // the _applied pattern (DeepReserves, FlowState, Tireless, etc.) apply their
    // bonuses immediately. The game loop's update chain (GameScene → Player →
    // Entity → SkillActivationComponent) runs asynchronously via rAF, so newly
    // registered effects won't receive their first update() until the next frame.
    // This explicit tick ensures passive bonuses are observable immediately after setup.
    player.skillActivation.update(0);
  }

  /** Reset to a clean state: no enemies, default player position. */
  reset(): void {
    this.setup({ player: { x: 400, y: 400 }, enemies: [] });
  }

  /** Dispatch a skill activation on the player. */
  dispatchSkill(skillId: string): void {
    if (!this.scene) return;
    this.scene.player.skillActivation.dispatch(skillId);
  }

  /** Directly apply a status effect to a tagged enemy, bypassing skill activation.
   *  Returns true if the effect was applied, false if the enemy/tracker was not found. */
  applyStatusToEnemy(tag: string, statusId: string, opts?: {
    durationMs?: number; intensity?: number; stacks?: number; sourceSkillId?: string;
  }): boolean {
    const enemy = this._enemyTags.get(tag);
    if (!enemy?.statusEffectTracker) return false;
    const result = enemy.statusEffectTracker.apply(statusId, {
      durationMs: opts?.durationMs,
      intensity: opts?.intensity,
      stacks: opts?.stacks ?? 1,
      sourceSkillId: opts?.sourceSkillId ?? '__test__',
    });
    return result !== null;
  }

  /** Set the player's facing direction from a degree angle (0 = right, 90 = down). */
  setPlayerFacing(angleDeg: number): void {
    if (!this.scene) return;
    const rad = (angleDeg * Math.PI) / 180;
    this.scene.player.facingDirection.x = Math.cos(rad);
    this.scene.player.facingDirection.y = Math.sin(rad);
  }

  /** Simulate directional movement input on the player. dx/dy: -1, 0, or 1. Both 0 = stop. */
  setPlayerMoving(dx: number, dy: number): void {
    if (!this.scene) return;
    if (dx === 0 && dy === 0) {
      this.scene.player.setSimMovementOverride(null);
    } else {
      this.scene.player.setSimMovementOverride({
        left: dx < 0,
        right: dx > 0,
        up: dy < 0,
        down: dy > 0,
      });
    }
  }

  /** Set the player's current stamina to a specific value (clamped to [0, max]). */
  setPlayerStamina(value: number): void {
    if (!this.scene) return;
    const stamina = this.scene.player.stamina;
    stamina.current = Math.max(0, Math.min(value, stamina.max));
  }

  /** Set the player's current HP to a specific value (clamped to [0, max]). */
  setPlayerHealth(value: number): void {
    if (!this.scene) return;
    this.scene.player.health.setHp(value);
  }

  /** Whitelist of debug flags that tests may enable. Keeps the surface minimal. */
  private static readonly ALLOWED_TEST_FLAGS = new Set(['alwaysSucceedSkills']);

  /** Enable or disable a single DebugConfig flag for test purposes.
   *  Re-enables the master toggle if needed — the flag is applied on top of all-off defaults. */
  setDebugFlag(flag: string, value: boolean): void {
    if (!SimulationBridge.ALLOWED_TEST_FLAGS.has(flag)) {
      throw new Error(`setDebugFlag: "${flag}" is not whitelisted for test use`);
    }
    // Ensure master is on so flags take effect
    if (!DebugConfig.enabled) {
      // First disable all gameplay flags to avoid god mode etc.
      DebugConfig.setCategoryAll('gameplay', false);
      DebugConfig.setEnabled(true);
    }
    // Set the individual flag
    const key = flag as DebugFlagKey;
    if (DebugConfig.flags[key] !== value) {
      DebugConfig.toggle(key);
    }
  }

  /** Force the stationary tracker to report the player as stationary for the given duration. */
  forceStationary(durationMs: number): void {
    if (!this.scene) return;
    this.scene.forceStationary(durationMs);
  }

  /** Tick all registered skill effects for the given delta. Workaround for the game loop's
   *  update chain not reaching SkillActivationComponent during deterministic frame pumping. */
  tickEffects(deltaMs: number): void {
    if (!this.scene) return;
    this.scene.player.skillActivation.update(deltaMs);
    // Also tick status effect trackers for player and enemies
    this.scene.player.statusEffectTracker?.update(deltaMs);
    for (const enemy of this.scene.enemyManager.all) {
      enemy.statusEffectTracker?.update(deltaMs);
    }
  }

  /** Query the factory's AI adapter directly — calls the named method with the given args. */
  queryFactoryAI(method: string, ...args: unknown[]): unknown {
    if (!this.scene) return null;
    const queries = this.scene.skillFactory.aiQueries;
    if (!queries) return null;
    const fn = (queries as unknown as Record<string, unknown>)[method];
    if (typeof fn !== 'function') return null;
    return fn(...args);
  }

  /** Build a serializable player state snapshot. */
  buildPlayerState(): PlayerState {
    if (!this.scene) return { x: 0, y: 0, facingAngleDeg: 0, movementSpeedMultiplier: 1, health: { current: 0, max: 0, concealedHealthRegenRate: 0 }, stamina: { current: 0, max: 0, drainMultiplier: 1, rechargeMultiplier: 1, walkingRechargeRate: 0.5 }, state: { isMovementLocked: false, isPetrified: false, isGhostForm: false, isKnockbackImmune: false, isStaggerImmune: false, damageReduction: 0 }, statusEffectDetails: [], dash: { charges: 0, maxCharges: 0, refillRequiresFullStamina: true, dashAttackStaminaRefundRatio: 0, suppressDashBreaksStealth: false, suppressDashGeneratesNoise: false, blinkIgnoresThinWalls: false } };
    const p = this.scene.player;
    const facingRad = Math.atan2(p.facingDirection.y, p.facingDirection.x);
    return {
      x: p.sprite.x,
      y: p.sprite.y,
      facingAngleDeg: (facingRad * 180) / Math.PI,
      movementSpeedMultiplier: p.movementSpeedMultiplier,
      health: {
        current: p.health.hp,
        max: p.health.max,
        concealedHealthRegenRate: p.concealedHealthRegenRate,
      },
      stamina: {
        current: p.stamina.current,
        max: p.stamina.max,
        drainMultiplier: p.stamina.drainMultiplier,
        rechargeMultiplier: p.stamina.rechargeMultiplier,
        walkingRechargeRate: p.stamina.walkingRechargeRate,
      },
      state: {
        isMovementLocked: p.playerState.isMovementLocked,
        isPetrified: p.playerState.isPetrified,
        isGhostForm: p.playerState.isGhostForm,
        isKnockbackImmune: p.playerState.isKnockbackImmune,
        isStaggerImmune: p.playerState.isStaggerImmune,
        damageReduction: p.playerState.damageReduction,
      },
      statusEffectDetails: (p.statusEffectTracker?.getAll() ?? []).map((e): StatusEffectDetail => ({
        id: e.def.id,
        remainingMs: e.remainingMs,
        intensity: e.intensity,
        stacks: e.stacks,
        sourceSkillId: e.sourceSkillId,
      })),
      dash: {
        charges: p.dash.charges,
        maxCharges: p.dash.maxCharges,
        refillRequiresFullStamina: p.dash.effectiveRefillRequiresFullStamina,
        dashAttackStaminaRefundRatio: p.stamina.dashAttackStaminaRefundRatio,
        suppressDashBreaksStealth: p.dash.suppressDashBreaksStealth,
        suppressDashGeneratesNoise: p.dash.suppressDashGeneratesNoise,
        blinkIgnoresThinWalls: p.dash.blinkIgnoresThinWalls,
      },
    };
  }

  /** Build a serializable state snapshot for a tagged enemy. */
  buildEnemyState(tag: string): EnemyState | null {
    const enemy = this._enemyTags.get(tag);
    if (!enemy) return null;
    return this.snapshotEnemy(tag, enemy);
  }

  /** Build state snapshots for all tagged enemies. */
  buildAllEnemyStates(): EnemyState[] {
    const states: EnemyState[] = [];
    for (const [tag, enemy] of this._enemyTags) {
      states.push(this.snapshotEnemy(tag, enemy));
    }
    return states;
  }

  /** Build a serializable world state snapshot (terrain, shields). */
  buildWorldState(): WorldState {
    if (!this.scene) return { terrainActiveCount: 0, fortifyShieldHp: 0 };
    const terrainCount = this.scene.getTerrainActiveCount();
    const fortify = this.scene.player.skillActivation.getEffect<FortifySkillEffect>('fortify');
    const shieldHp = fortify ? fortify.shieldHp : 0;
    return {
      terrainActiveCount: terrainCount,
      fortifyShieldHp: shieldHp,
    };
  }

  // ── Scene system queries (for wiring smoke tests) ────────────────

  /** Query the UpgradeEffectResolver for a skill's resolved damage multiplier. */
  queryUpgradeDamageMultiplier(skillId: string): number {
    if (!this.scene) return 1.0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- test bridge accesses private scene fields
    const scene = this.scene as any;
    return scene.upgradeResolver.getResolvedDamageMultiplier(skillId);
  }

  /** Query the number of registered death triggers. */
  queryDeathTriggerCount(): number {
    if (!this.scene) return 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- test bridge accesses private fields
    const scene = this.scene as any;
    return (scene.deathTriggerSystem.triggers as Map<string, unknown>).size;
  }

  /** Query the number of registered cooldown hooks. */
  queryCooldownHookCount(): number {
    if (!this.scene) return 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- test bridge accesses private fields
    const scene = this.scene as any;
    return (scene.cooldownHookSystem.hooks as Map<string, unknown>).size;
  }

  /** Query the EnemyBuffManager's damage multiplier for a tagged enemy. */
  queryBuffDamageMultiplier(tag: string): number {
    if (!this.scene) return 1.0;
    const enemy = this._enemyTags.get(tag);
    if (!enemy) return 1.0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- test bridge accesses private scene fields
    const scene = this.scene as any;
    return scene.buffManager.getDamageMultiplier(enemy);
  }

  /** Query the ChargeSystem for a charge type's current state. */
  queryChargeState(chargeId: string): { current: number; maxCharge: number } | null {
    if (!this.scene) return null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- test bridge accesses private scene fields
    const scene = this.scene as any;
    const instance = scene.chargeSystem.get(chargeId);
    if (!instance) return null;
    return { current: instance.current, maxCharge: instance.maxCharge };
  }

  /** Add charge to a registered charge type (for wiring smoke tests). */
  addCharge(chargeId: string, amount: number): void {
    if (!this.scene) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- test bridge accesses private scene fields
    const scene = this.scene as any;
    scene.chargeSystem.addCharge(chargeId, amount);
  }

  /** Query the StationaryTracker's accumulated stationary duration. */
  queryStationaryDurationMs(): number {
    if (!this.scene) return 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- test bridge accesses private scene fields
    const scene = this.scene as any;
    return scene.stationaryTracker.getStationaryDurationMs();
  }

  /** Query the number of active gravity wells. */
  queryGravityWellCount(): number {
    if (!this.scene) return 0;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- test bridge accesses private scene fields
    const scene = this.scene as any;
    return scene.gravityPullSystem.getAll().length;
  }

  /** Add a gravity well directly (for wiring smoke tests). */
  addGravityWell(config: { id: string; x: number; y: number; pullRadiusTiles: number; pullStrengthTilesPerSec: number; durationMs: number; sourceSkillId: string }): string {
    if (!this.scene) return '';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- test bridge accesses private scene fields
    const scene = this.scene as any;
    return scene.gravityPullSystem.addWell({
      id: config.id,
      position: { x: config.x, y: config.y },
      pullRadiusTiles: config.pullRadiusTiles,
      pullStrengthTilesPerSec: config.pullStrengthTilesPerSec,
      durationMs: config.durationMs,
      sourceSkillId: config.sourceSkillId,
    });
  }

  /** Query the OrbitalDebrisSystem's current state. */
  queryOrbitalDebrisState(): { count: number; isActive: boolean } {
    if (!this.scene) return { count: 0, isActive: false };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- test bridge accesses private scene fields
    const scene = this.scene as any;
    return { count: scene.orbitalDebrisSystem.count, isActive: scene.orbitalDebrisSystem.isActive };
  }

  /** Start orbital debris accumulation directly (for wiring smoke tests). */
  startOrbitalAccumulation(): void {
    if (!this.scene) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- test bridge accesses private scene fields
    const scene = this.scene as any;
    scene.orbitalDebrisSystem.startAccumulating();
  }

  /** Query the TemporalEchoStateSystem's current state. */
  queryTemporalEchoState(): { isRecording: boolean; snapshot: { x: number; y: number; hp: number } | null } {
    if (!this.scene) return { isRecording: false, snapshot: null };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- test bridge accesses private scene fields
    const scene = this.scene as any;
    const sys = scene.temporalEchoStateSystem;
    const snap = sys.snapshot;
    return {
      isRecording: sys.isRecording,
      snapshot: snap ? { x: snap.x, y: snap.y, hp: snap.hp } : null,
    };
  }

  /** Begin temporal echo recording directly (for wiring smoke tests). */
  beginTemporalEchoRecording(x: number, y: number, hp: number, windowMs: number): void {
    if (!this.scene) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- test bridge accesses private scene fields
    const scene = this.scene as any;
    scene.temporalEchoStateSystem.beginRecording(x, y, hp, windowMs);
  }

  /** Returns true when no skill or attack is in an active phase — the scene has "settled". */
  isSettled(): boolean {
    if (!this.scene) return true;
    if (this.scene.player.skillActivation.hasActiveEffects()) return false;
    if (this.scene.player.combat.isAttacking) return false;
    return true;
  }

  // ── Internal helpers ──────────────────────────────────────────────

  private snapshotEnemy(tag: string, enemy: Enemy): EnemyState {
    const allEffects = enemy.statusEffectTracker?.getAll() ?? [];
    return {
      tag,
      x: enemy.sprite.x,
      y: enemy.sprite.y,
      hp: enemy.healthComponent.hp,
      maxHp: enemy.healthComponent.max,
      isDead: enemy.healthComponent.isDead,
      statusEffects: allEffects.map(e => e.def.id),
      statusEffectDetails: allEffects.map((e): StatusEffectDetail => ({
        id: e.def.id,
        remainingMs: e.remainingMs,
        intensity: e.intensity,
        stacks: e.stacks,
        sourceSkillId: e.sourceSkillId,
      })),
      velocity: { x: enemy.body.velocity.x, y: enemy.body.velocity.y },
      aggroLevel: enemy.ai.aggro,
      aiState: enemy.ai.state,
    };
  }
}

/** Reverse-lookup: find the entityId key for a given state object in the entities map. */
function findEntityId(entities: Map<string, unknown>, state: unknown): string | undefined {
  for (const [id, s] of entities) {
    if (s === state) return id;
  }
  return undefined;
}
